import sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64';
import UTF8 from 'crypto-js/enc-utf8';
import uuid from 'uuid';
import { createHashHistory } from 'history';
import WebUtils from './WebUtils';
export const {
  config: defaultAppConfig
} = window;
const HYRULE_AUTHENTICATED = 'hyruleAuthenticated';
const HYRULE_IMPLICIT_AUTHENTICATED = 'hyruleAuth';
let accessToken = null;
let scope = null;

/**
 * Issues a Authorization Code Grant Type with PKCE redirect, using the Hyrupe App Config found at window.config
 */
export const loginRedirect = () => loginRedirectWithConfig(defaultAppConfig);

/**
 * Issues a Authorization Code Grant Type with PKCE redirect, using the provided Hyrule App Config
 * @param hyruleAppConfig {HyruleAppConfig} - The config to use for issuing the login redirect
 */
export const loginRedirectWithConfig = hyruleAppConfig => {
  const redirect = getBaseRedirectUrl();

  // Generate the PKCE verifier
  const verifier = uuid.v4();

  // Generate a key to save the PKCE verifier in session storage
  const sessionStorageId = uuid.v4();
  sessionStorage.setItem(sessionStorageId, verifier);
  const codeChallenge = generateCodeChallenge(verifier);
  const stateString = createStatePropertiesString(sessionStorageId);
  window.location.replace(`${hyruleAppConfig.authServerBaseUri}/oauth/authorize?response_type=code&client_id=${hyruleAppConfig.clientId}&redirect_uri=${redirect}&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${stateString}`);
};
export const logoutRedirect = currentToken => {
  logoutRedirectWithConfig(currentToken, defaultAppConfig);
};
export const logoutRedirectWithConfig = (currentToken, hyruleAppConfig) => {
  const redirect = getBaseRedirectUrl();
  window.location.replace(`${hyruleAppConfig.authServerBaseUri}/logout?client_id=${hyruleAppConfig.clientId}&redirect_uri=${redirect}&access_token=${currentToken}`);
};
export const getBaseRedirectUrl = () => {
  const {
    location
  } = window;
  let url = `${location.protocol}//${location.host}/`;
  const pathList = location.pathname.split('/');
  if (pathList[1]) {
    url = url.concat(`${pathList[1]}/`);
  }
  return url;
};

/**
 * The method that trades an authorization code for an access token. This method assumes you have the standard Hyrule
 * front-end app config that defines the authServerBaseUri and clientId
 * @param {String} authCode - The OAuth 2 authorization code
 * @param {String} sessionStorageId - The session storage key where the PKCE verifier was saved
 * @returns {Promise<HyruleToken>} - Returns an object that contains the accessToken and the scope
 */
export const tradeAuthCode = async (authCode, sessionStorageId) => {
  const {
    authServerBaseUri,
    clientId
  } = defaultAppConfig;
  return tradeAuthCodeWithConfig(authCode, {
    authServerBaseUri,
    clientId
  }, sessionStorageId);
};

/**
 * The method that trades an authorization code for an access token.
 * @param {String} authCode - The OAuth 2 authorization code
 * @param {String} hyruleAppConfig - The HyruleAppConfig containing the auth server base URI and the client ID
 * @param {String} sessionStorageId - The session storage key where the PKCE verifier was saved
 * @returns {Promise<HyruleToken>} - Returns an object that contains the accessToken and the scope
 */
export const tradeAuthCodeWithConfig = async (authCode, hyruleAppConfig, sessionStorageId) => {
  const href = window.location.href.split('?');
  if (href.length > 1) {
    replaceWindowHistory(null);
  }
  const codeVerifier = sessionStorage.getItem(sessionStorageId);
  const redirectUri = getBaseRedirectUrl();
  const accessTokenResponse = await WebUtils.tradeAuthCode(`${hyruleAppConfig.authServerBaseUri}/oauth/token`, authCode, redirectUri, codeVerifier, hyruleAppConfig.clientId);
  if (!accessTokenResponse) {
    throw new Error('Hyrule Error: Got an invalid response while trading access token');
  }
  const result = await accessTokenResponse.json();

  // Parse the access token and scopes
  const newAccessToken = result.access_token;
  const scopeString = result.scope;

  // Save them in memory
  accessToken = newAccessToken;
  scope = scopeString;

  // This will be the flag to other Hyrule apps that the user should already be authenticated
  localStorage.setItem(HYRULE_AUTHENTICATED, 'true');

  // Remove the PKCE key that was used
  sessionStorage.removeItem(sessionStorageId);
  const scopes = scopeString ? scopeString.split(' ') : [];
  return {
    accessToken,
    scopes
  };
};
export const getAuthCode = () => accessToken;
export const getScope = () => scope;
export const getScopes = () => {
  const scopeString = getScope();
  if (scopeString !== null) {
    return scopeString ? scopeString.split(' ') : [];
  }
  return [];
};
export const hasScope = allowedScopes => {
  const userScopes = getScopes();
  return allowedScopes.some(allowedScope => userScopes.indexOf(allowedScope) > -1);
};

/**
 * Checks to see if any of this Users scopes ends with any of the scopeSuffixes
 * @param {string[]} scopeSuffixes array of String scope suffixes to check
 * @returns {boolean} true if one of the User scopes end in one of the scopeSuffixes, false otherwise.
 */
export const hasScopeSuffixes = scopeSuffixes => {
  if (!scopeSuffixes) return false;
  const userScopes = getScopes();
  return userScopes.some(userScope => scopeSuffixes.some(scopeSuffix => userScope.endsWith(scopeSuffix)));
};
export const logout = () => {
  localStorage.setItem(HYRULE_AUTHENTICATED, 'false');
  accessToken = null;
  scope = null;
  createHashHistory().push('/logged-out');
};

/**
 * A function that returns a boolean if the user is expected to be authenticated or not
 * @returns {boolean} Returns true if the user has completed a Hyrule Auth Server authentication
 */
export const isUserAuthenticated = () => localStorage.getItem(HYRULE_AUTHENTICATED) === 'true' || localStorage.getItem(HYRULE_IMPLICIT_AUTHENTICATED) !== null;

/**
 * Generates the code challenge for PKCE
 * Reference: https://tools.ietf.org/html/rfc7636#appendix-A
 *
 * @param {String} verifier The code verifier to generate a challenge for
 * @returns {string} Returns the S256 challenge
 */
export const generateCodeChallenge = verifier => {
  const hashedVerifier = sha256(verifier);
  return Base64.stringify(hashedVerifier).split('=')[0].replace(/\+/g, '-').replace(/\//g, '_');
};

/**
 * Creates a state string to be used in the OAuth redirect flow.
 * The user's route is saved (as long as it's not / or /logged-out) so that they land on the same page after logging in
 * The sessionStorageId is saved so that the PKCE key can be retrieved from session storage after a redirect
 * @param {String} sessionStorageId - The session storage key where the PKCE verifier is stored
 * @returns {String} The Base64 encoded state string
 */
export const createStatePropertiesString = sessionStorageId => {
  const stateProperties = {
    sessionStorageId
  };

  // Keep track of the hash location so that the user can land on the same page post-redirect
  const {
    hash
  } = window.location;
  if (hash) {
    const route = hash.split('#')[1];
    if (route && route !== '/' && route !== '/logged-out') {
      stateProperties.route = route;
    }
  }
  stateProperties.sessionStorageId = sessionStorageId;
  const stateJsonString = JSON.stringify(stateProperties);
  return Base64.stringify(UTF8.parse(stateJsonString));
};

/**
 * Parses the encoded state string into an object
 * @param {String} encodedStateString - The Base64 encoded state string
 * @returns {object} An object that represents the state. It should include the route and the sessionStorageId.
 */
export const parseEncodedStateProperties = encodedStateString => {
  if (encodedStateString) {
    const stateParameter = Base64.parse(encodedStateString);
    const stateString = stateParameter.toString(UTF8);
    return JSON.parse(stateString);
  }
  return {
    sessionStorageId: ''
  };
};

/**
 * Gets the authenticated user's information, using the config found at the default window.config location
 * @returns {Promise<UserInfo>} - Returns an object that represents the user info
 */
export const getUserInformation = async () => getUserInformationWithConfig(defaultAppConfig);

/**
 * Gets the authenticated user's information, using the provided Hyrule App Config
 * @param hyruleAppConfig {HyruleAppConfig} - The Hyrule Application Configuration to use
 * @returns {Promise<UserInfo>} - Returns an object that represents the user info
 */
export const getUserInformationWithConfig = async hyruleAppConfig => WebUtils.requestJson(`${hyruleAppConfig.authServerBaseUri}/api/users/me`, 'GET', null);

/**
 * Sets the authenticated user's information, using the config found at the default window.config location
 * @param locale - user's preferred language setting
 * @param timezone - user's preferred timezone
 * @returns {Promise<UserInfo>} - Returns an object that represents the user info
 */
export const updateUserInformation = async (locale, timezone) => updateUserInformationWithConfig(defaultAppConfig, locale, timezone);

/**
 * Sets the authenticated user's information, using the provided Hyrule App Config
 * @param hyruleAppConfig {HyruleAppConfig} - The Hyrule Application Configuration to use
 * @param locale - user's preferred language setting
 * @param timezone - user's preferred timezone
 * @returns {Promise<UserInfo>} - Returns an object that represents the user info
 */
export const updateUserInformationWithConfig = async (hyruleAppConfig, locale, timezone) => {
  const request = {
    locale,
    timezone
  };
  return WebUtils.requestJson(`${hyruleAppConfig.authServerBaseUri}/api/users/me/settings`, 'PUT', JSON.stringify(request));
};

/**
 * Replaces the window history with an optional path.
 * If no path is provided, just the root of the path will be used.
 * @param path The path to push to the window history
 */
export const replaceWindowHistory = path => {
  const href = window.location.href.split('?');
  if (href.length > 1) {
    const newPath = path ? href[0] + path : href[0];
    window.history.replaceState({}, document.title, newPath);
  }
};